import { Component, ElementRef, HostBinding, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';

import { I18nService } from './core/services/i18n.service';
import { ConfigService } from './core/services/config.service';
import { StorefrontService } from './core/services/storefront.service';
import { CartService } from './core/services/cart.service';
import { UiService } from './core/services/ui.service';
import { SessionService } from './core/services/session.service';
import { AuthService } from './core/services/auth.service';
import { NavService } from './core/services/nav.service';
import { ViewportService } from './core/services/viewport.service';
import { PreviewService } from './core/preview/preview.service';

/** Screens that show the bottom tab bar / desktop nav highlight. */
const TAB_SCREENS = ['home', 'menu', 'orders', 'account'];
/** Screens where the sticky "view cart" bar may appear. */
const CART_BAR_SCREENS = ['home', 'menu', 'search'];

/** Phone screens that keep the fixed full-screen frame instead of document scrolling. */
const FRAME_SCREENS = ['dinein'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit {
  /** Desktop layout (top nav + footer + multi-column screens) from 900px. */
  isDesktop = this.vp.isDesktop;

  /** Desktop page scroller (screen + footer) — drives the header's over-hero look. */
  @ViewChild('page') private pageEl?: ElementRef<HTMLElement>;
  /** Path of the current page (no query) — a change means scroll back to the top. */
  private lastPath = '';
  /** true until the page scrolls past the first 30px (header stays transparent over the hero). */
  pageAtTop = true;

  onPageScroll(): void {
    const top = (this.pageEl?.nativeElement.scrollTop ?? 0) <= 30;
    if (top !== this.pageAtTop) this.pageAtTop = top;
  }

  /** Phones scroll the document (Safari full height) — the host drops its fixed height. */
  @HostBinding('class.bb-doc-scroll') docScroll = false;

  /** Announcement bar messages (desktop: across the top, above the header). */
  get announcements(): string[] { return this.sf.announcements; }

  /** briefly true while re-mounting screens after a language switch */
  reloading = false;

  /** Current top-level screen id, derived from the route. */
  screen$!: Observable<string>;
  count$ = this.cart.count$;
  total$ = this.cart.total$;
  toast$ = this.ui.toast$;

  constructor(
    public i18n: I18nService,
    public config: ConfigService,
    private sf: StorefrontService,
    private cart: CartService,
    private ui: UiService,
    private session: SessionService,
    private auth: AuthService,
    private router: Router,
    private navSvc: NavService,
    private vp: ViewportService,
    private preview: PreviewService,
  ) {}

  ngOnInit(): void {
    this.vp.isDesktop$.subscribe((d) => (this.isDesktop = d));

    // Live preview (admin Storefront Settings): every draft the admin pushes is
    // applied to the config, then the screen is rebuilt in place, keeping the scroll.
    if (this.preview.active) {
      document.documentElement.classList.add('sf-in-preview');
      this.sf.changed$.subscribe(() => {
        const scroller = document.scrollingElement;
        const top = scroller?.scrollTop ?? 0;
        this.remountScreen();
        setTimeout(() => scroller?.scrollTo({ top }), 0);
      });
    }

    // Drop stale "logged in" state with no real token (e.g. from earlier builds).
    if (this.session.loggedIn && !this.session.token) this.session.signOut();

    this.screen$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.screenFromUrl()),
      startWith(this.screenFromUrl()),
    );

    // Every move to another page starts at its top (phones scroll the document,
    // desktop scrolls .bb-page). A query-only change on the same page — e.g. the
    // menu's ?cat= — keeps its position. Then re-check the header look.
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const path = e.urlAfterRedirects.split(/[?#]/)[0];
      if (path !== this.lastPath) {
        window.scrollTo(0, 0);
        if (this.pageEl) this.pageEl.nativeElement.scrollTop = 0;
      }
      this.lastPath = path;
      setTimeout(() => this.onPageScroll());
    });
    // Phones let the document scroll; the camera scanner keeps its fixed full-screen frame.
    combineLatest([this.screen$, this.vp.isDesktop$]).subscribe(([screen, desktop]) => {
      const doc = !desktop && !this.config.storeOff && !FRAME_SCREENS.includes(screen);
      if (doc !== this.docScroll) { this.docScroll = doc; window.scrollTo(0, 0); }
    });

    // Re-mount the routed screen so OnPush bodies re-render after a
    // language switch, an auth change, or an order-type / branch change.
    this.i18n.lang$.pipe(skip(1)).subscribe(() => this.remountScreen());
    this.session.loggedIn$.pipe(skip(1), distinctUntilChanged()).subscribe(() => this.remountScreen());
    combineLatest([this.session.fulfillment$, this.session.branch$]).pipe(
      map(([mode, branch]) => `${mode}|${branch.id}`),
      distinctUntilChanged(),
      skip(1),
    ).subscribe(() => this.remountScreen());

    // When the operator disables Home, bounce any landing on it to the Menu.
    if (!this.config.homeEnabled) {
      this.screen$.subscribe((s) => { if (s === 'home') this.router.navigate(['/menu']); });
    }

    // Validate any persisted auth token; sign out if it's no longer valid.
    if (this.session.token) {
      this.auth.me().subscribe({
        next: (customer) => this.session.setCustomer(customer),
        error: () => this.session.signOut(),
      });
    }
  }

  get dir(): 'rtl' | 'ltr' { return this.i18n.dir; }
  get lang(): 'en' | 'ar' { return this.i18n.lang; }
  get brandVars(): Record<string, string> { return this.config.brandVars(this.i18n.isRtl); }
  get homeOn(): boolean { return this.config.homeEnabled; }
  get dineInSupported(): boolean { return this.config.supportedOrderTypes.includes('dinein'); }

  showTabs(screen: string): boolean { return TAB_SCREENS.includes(screen); }
  /** No cart in the admin live preview — it is read-only. */
  showCartBar(screen: string): boolean { return !this.preview.active && CART_BAR_SCREENS.includes(screen); }
  /** Dine-in FAB shows on home/menu only when the brand supports dine-in. */
  showDineFab(screen: string): boolean {
    if (!this.dineInSupported || this.preview.active) return false;
    // The desktop menu offers "Scan to dine in" in its category rail instead.
    if (this.isDesktop) return screen === 'home';
    return screen === 'home' || screen === 'menu';
  }

  /** Maintenance "Try again" — re-fetch the brand config by reloading. */
  retry(): void { window.location.reload(); }
  /** This site's address, shown on the "store not found" page. */
  get host(): string { return window.location.hostname; }

  nav(screen: string): void {
    // Desktop search is a palette over the current page, not a separate screen.
    if (screen === 'search' && this.isDesktop) { this.ui.openSearch(); return; }
    this.navSvc.nav(screen);
  }
  goCart(): void { this.navSvc.nav('cart'); }
  // Route through the special /fulfillment guard: opens the sheet, keeps the current screen.
  openFulfillment(): void { this.navSvc.nav('fulfillment'); }
  /** Dine-in FAB → QR scanner (resolves table + branch from the backend). Requires
   *  sign-in first; after login it continues straight to the scanner. */
  openDineIn(): void { this.ui.startDineIn(); }
  toggleLang(): void { this.i18n.toggle(); }

  private screenFromUrl(): string {
    const seg = this.router.url.split('?')[0].split('/').filter(Boolean)[0];
    return seg || 'home';
  }

  private remountScreen(): void {
    this.reloading = true;
    setTimeout(() => (this.reloading = false), 0);
  }
}
